import Controller, { OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ActionAPI, CheckboxAPI, FormAPI, InputAPI, ModalProps, ModalService, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { Value } from '@philips-internal/oswald-ui-core';

import { Inject } from '@odinjs/odin';

import BaseController from '../../../BaseController';
import { FragmentNames } from '../../../../codegen/index';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { ScoreNrsViewerAliases } from '../../../../codegen/constants/aliases/score-nrs-viewer-aliases';
import { ApplicationRoute } from '../../../../constants/routes';
import { DomainType, ScoreNRSResponse } from '../../score-types';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { ScoreAndIndexesService, ScoreListContext } from '../../scores-list/scores-list-service';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { HealthProfessionalUser } from '../../../../app/authentication';

@Controller({ fragment: FragmentNames.SCORE_NRS_VIEWER })
export default class ScorenNrsViewerController extends BaseController {

  @Inject(ScoreNrsViewerAliases.FORM_SCORE_NRS_VIEWER)
  private formAPI!: FormAPI;

  @Inject(ScoreNrsViewerAliases.ACTION_INACTIVATE_SCORE_NRS)
  private actionInactivateAPI!: ActionAPI;

  @Inject(ScoreNrsViewerAliases.ACTION_NEW_SCORE_NRS)
  private actionNewAPI!: ActionAPI;

  @Inject(ScoreNrsViewerAliases.ACTION_REFERENCE_SCORE_NRS)
  private actionReferenceAPI!: ActionAPI;

  @Inject(ScoreNrsViewerAliases.INPUT_EVALUATION_DATE)
  private inputEvaluationDateAPI!: InputAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_AGE)
  private ageTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_HEIGHT)
  private heightTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_WEIGHT)
  private weightTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_WEIGHT_LOSS)
  private weightLossTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_IMC)
  private imcTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_NUTRITIONAL_STATUS_DOMAIN)
  private nutritionalStatusDomainTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_DISEASE_SEVERITY_DOMAIN)
  private diseaseSeverityDomainTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_PUNCTUATION)
  private punctuationTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_RESULT_NRS)
  private resultNrsTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_NOTE)
  private noteTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_USUAL_WEIGHT)
  private usualWeightTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.TEXT_PROFESSIONAL)
  private professionalTextAPI!: TextAPI;

  @Inject(ScoreNrsViewerAliases.CHECKBOX_IE_WEIGHT_LOSS)
  private weightLossLastMonthCheckboxAPI!: CheckboxAPI

  @Inject(ScoreNrsViewerAliases.CHECKBOX_REDUCED_FEEDING)
  private reducedFeedingCheckboxAPI!: CheckboxAPI

  @Inject(ScoreNrsViewerAliases.CHECKBOX_UTI)
  private utiCheckboxAPI!: CheckboxAPI

  @OnMount({ target: ScoreNrsViewerAliases.LAYOUT_SCORE_NRS_VIEWER })
  handleMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    applicationContext.popNavigationItem();
    this.context.set({encounterId, patientId});
    ScoreListContext.currentTab = ScoreType.SCORE_NRS
  }

  @OnMount({ target: ScoreNrsViewerAliases.FORM_SCORE_NRS_VIEWER })
  handleMountForm(): void {
    this.formAPI.setLoading(true);
    this.fetchScoreNRS()
  }

  @OnClick({ target: ScoreNrsViewerAliases.ACTION_REFERENCE_SCORE_NRS })
  handleClickReference(): void {
    this.getReference();
  }

  @OnClick({ target: ScoreNrsViewerAliases.ACTION_NEW_SCORE_NRS })
  handleClickNew(): void {
    const { encounterId, patientId,  } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    applicationContext.redirect(ApplicationRoute.SCORE_NRS_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnClick({ target: ScoreNrsViewerAliases.ACTION_INACTIVATE_SCORE_NRS})
  handleClickInactivate(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-inactivate',
      id: 'score-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      idScoreIndexes,
    } as Record<string, Value>

    ModalService.openDefault(modalProperties);
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-nrs-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private getReference(): void {
    const scoreType = 'pews';
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-reference',
      id: 'score-reference',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.reference-pain-scale-value',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      scoreType,
    }

    ModalService.openFullscreen(modalProperties);
  }

  private fetchScoreNRS(): Promise<void> {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    const allowInfo = InformationBarUtils.allowInfo && ScoreAndIndexesService.isAllowEdit;

    return HttpService.get(Endpoint.SCORE_NRS, { sequenceNumber: idScoreIndexes})
      .then(response => {
        const record = response.body as ScoreNRSResponse;
        const user = foundationContext.getUser() as HealthProfessionalUser;
        const dateEvaluation = record.evaluationDate || '';
        const age = record.age || '';
        const weight = record.weight || '';
        const height = record.height || '';
        const professionalName = record.professionalName  || '';
        const imc = record.imc || '';
        const weightLoss = record.weightLoss || '';
        const usualWeight = record.usualWeight as unknown as string || '';
        const nutritionalStatusDomain = record.nutritionalStatusDomain as DomainType;
        const diseaseSeverityDomain = record.diseaseSeverityDomain as DomainType;
        const resultNrs = record.resultNrs || '';
        const punctuation = record.punctuation as unknown as string || '0';
        const note = record.note || '';
        const showInactivate = record.professionalCode === user.physicalPersonId && Boolean(record.showInactivate) && allowInfo;

        this.inputEvaluationDateAPI.setValue(DateFormatterUtil.timeStamp(String(dateEvaluation)))
        this.ageTextAPI.setValue(String(age))
        this.weightTextAPI.setValue(String(weight))
        this.weightLossTextAPI.setValue(String(weightLoss))
        this.heightTextAPI.setValue(String(height))
        this.imcTextAPI.setValue(String(imc))
        this.usualWeightTextAPI.setValue(String(usualWeight))
        this.nutritionalStatusDomainTextAPI.setValue(nutritionalStatusDomain.description)
        this.diseaseSeverityDomainTextAPI.setValue(diseaseSeverityDomain.description)
        this.resultNrsTextAPI.setValue(resultNrs)
        this.punctuationTextAPI.setValue(String(punctuation))
        this.noteTextAPI.setValue(note)
        this.professionalTextAPI.setValue(professionalName)
        this.utiCheckboxAPI.setValue(BooleanUtils.isTrueValue(record.uti))
        this.weightLossLastMonthCheckboxAPI.setValue(BooleanUtils.isTrueValue(record.weightLossLastMonth))
        this.reducedFeedingCheckboxAPI.setValue(BooleanUtils.isTrueValue(record.reducedFeeding))
        this.actionInactivateAPI.setEnabled(showInactivate);

      }).catch(error => this.showToast(error?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formAPI.setLoading(false);
        this.actionNewAPI.setEnabled(allowInfo);
        this.actionReferenceAPI.setEnabled(allowInfo);
      });
  }

  @OnUnmount(ScoreNrsViewerAliases.LAYOUT_SCORE_NRS_VIEWER)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_NRS;
  }
}
